import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { Stepper } from "../components/Stepper";
import { Views, FormData } from "../types";
import { applicationConditions } from "../constants";
import { Button, ButtonVariant, TextArea } from "shared/components";

import { ThumbsDown, ThumbsUp } from "untitledui-js-base";
import { cx } from "shared/utils";
import { useAuthContext } from "providers/Auth";
import { Applicant, DecisionAction } from "shared/types/application";

export const EmailView = ({
  formData,
  setFormData,
  view,
  flow,
  address,
  applicants
}: {
  formData: FormData;
  setFormData: Dispatch<SetStateAction<FormData>>;
  view: Views;
  flow: Views[];
  address: string;
  applicants: Applicant[];
}) => {
  const [editMode, setEditMode] = useState(false);
  const { partner } = useAuthContext();
  const [additionalText, setAdditionalText] = useState<string>("");

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!formData.additionalEmailText) {
      const text =
        formData.decision === DecisionAction.Approve
          ? `We are excited to inform you that your ${applicants.length > 1 ? "group's" : ""} application for ${address} has been approved.`
          : formData.decision === DecisionAction.ConditionalApprove
            ? `We are excited to inform you that your ${applicants.length > 1 ? "group's" : ""} application for ${address} has been conditionally approved. 
      
      Key factors that adversely affected your application:
      ${[
        ...new Set(
          formData.conditions
            .map(
              (c) =>
                `• ${applicationConditions.find((ac) => ac.id === c)?.text}`
            )
            .concat(
              formData.additionalCondition
                ? `• ${formData.additionalCondition}`
                : ""
            )
        )
      ].join("\n")}`
            : `We are sorry to inform you that your ${applicants.length > 1 ? "group's" : ""} application for ${address} has been declined.

      Key factors that adversely affected your application:
      ${[
        ...new Set(
          formData.conditions
            .map(
              (c) =>
                `• ${applicationConditions.find((ac) => ac.id === c)?.text}`
            )
            .concat(
              formData.additionalCondition
                ? `• ${formData.additionalCondition}`
                : ""
            )
        )
      ].join("\n")}`;

      setFormData((prev) => ({
        ...prev,
        additionalEmailText: text
      }));
      setAdditionalText(text);
    } else setAdditionalText(formData.additionalEmailText);
  }, [formData.decision]);

  return (
    <div className="flex flex-col h-full">
      <Stepper view={view} flow={flow} />

      <div className="p-6 min-h-full">
        <div className="min-h-full bg-background-900 border border-alpha-100 rounded-lg p-4 md:p-8">
          <div className="mx-auto max-w-[640px] w-full bg-white border border-alpha-100 shadow-sm px-3 md:px-12 py-10 rounded-lg">
            {formData.decision === DecisionAction.Decline ? (
              <ThumbsDown className="h-[50px] w-[50px] stroke-danger-500" />
            ) : (
              <ThumbsUp
                className={cx(
                  "h-[50px] w-[50px]",
                  formData.decision === DecisionAction.ConditionalApprove
                    ? "stroke-warning-500"
                    : "stroke-success-500"
                )}
              />
            )}

            <h2 className="my-6 text-2xl font-bold text-content-1000">
              Application{" "}
              {formData.decision === DecisionAction.Decline
                ? "declined"
                : formData.decision === DecisionAction.ConditionalApprove
                  ? "conditionally approved"
                  : "approved"}
            </h2>

            <p className="text-base text-content-500 mb-2">
              {/* Not sure if needs to be changed to getFullName */}
              Dear {applicants[0]?.first_name} {applicants[0]?.last_name},
            </p>

            <p className="text-base text-content-500 border-b border-alpha-40 pb-6 mb-6">
              {partner?.name}{" "}
              {formData.decision === DecisionAction.Decline
                ? "has declined"
                : formData.decision === DecisionAction.ConditionalApprove
                  ? "has conditionally approved"
                  : "has approved"}{" "}
              your rental application for{" "}
              <span className="font-bold text-content-500">{address}.</span>
            </p>

            <div className="border-b border-alpha-40 pb-6 mb-6">
              {editMode ? (
                <div>
                  <TextArea
                    id="additionalText"
                    ref={textAreaRef}
                    label={`Message from ${partner?.name}{" "}
                    ${
                      formData.decision === DecisionAction.Decline
                        ? "for declined declined"
                        : formData.decision ===
                            DecisionAction.ConditionalApprove
                          ? "has been conditionally approved"
                          : "has been approved"
                    }`}
                    value={additionalText}
                    onChange={(e) => setAdditionalText(e.target.value)}
                    className="whitespace-pre-line"
                    placeholder="Enter additional text here"
                    rows={5}
                  />
                  <div className="flex gap-2 items-center justify-end">
                    <Button
                      onClick={() => {
                        if (textAreaRef.current)
                          textAreaRef.current.value =
                            formData.additionalEmailText;

                        setAdditionalText(formData.additionalEmailText);
                        setEditMode(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={ButtonVariant.PRIMARY}
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          additionalEmailText: additionalText
                        }));
                        setEditMode(false);
                      }}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="whitespace-pre-line p-5 rounded-lg flex gap-2 bg-background-900">
                  <p className="text-sm text-content-500">
                    <p className="font-bold mb-2">
                      Message from {partner?.name}:
                    </p>

                    <p>{additionalText}</p>
                  </p>
                  <Button
                    className="shrink-0"
                    onClick={() => {
                      setEditMode(true);
                    }}
                  >
                    Edit
                  </Button>
                </div>
              )}
            </div>

            {formData.decision !== DecisionAction.Approve ? (
              <div className="border-b border-alpha-40 pb-6 mb-6">
                <p className="text-sm text-content-500 whitespace-pre-line">
                  {applicants[0]?.disclosures}
                </p>
              </div>
            ) : null}

            <p className="text-sm text-content-500 ">Regards, </p>
            <p className="text-sm text-content-500 font-bold mb-4">Team Boom</p>

            <p className="text-sm text-content-300">
              You are receiving this email because {partner?.name} is using Boom
              to power their application experience.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
